import type { DwfInflow } from "../model/inp/dwf.ts";
import type { Pattern } from "../model/inp/patterns.ts";
import type { Pollutant } from "../model/inp/pollutants.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

const PATTERN_SLOT_LABEL_KEYS = [
  "dwf.pattern1",
  "dwf.pattern2",
  "dwf.pattern3",
  "dwf.pattern4",
] as const;

/** The 4 pattern slots accept any defined pattern regardless of its type — matched by its stored type at simulation time, not by slot position. */
export function DwfEditor({
  nodeName,
  pollutants,
  patterns,
  inflows,
  onChange,
}: {
  nodeName: string;
  pollutants: Pollutant[];
  patterns: Pattern[];
  inflows: DwfInflow[];
  onChange: (rows: DwfInflow[]) => void;
}) {
  const t = useT();
  const usedNames = new Set(inflows.map((i) => i.constituent));
  const availableToAdd = ["FLOW", ...pollutants.map((p) => p.name)].filter(
    (name) => !usedNames.has(name)
  );

  function addRow() {
    if (availableToAdd.length === 0) return;
    onChange([
      ...inflows,
      { node: nodeName, constituent: availableToAdd[0], avgValue: 0, patterns: ["", "", "", ""] },
    ]);
  }

  function setConstituent(oldConstituent: string, constituent: string) {
    onChange(inflows.map((i) => (i.constituent === oldConstituent ? { ...i, constituent } : i)));
  }

  function setAvgValue(constituent: string, avgValue: number) {
    onChange(inflows.map((i) => (i.constituent === constituent ? { ...i, avgValue } : i)));
  }

  function setPattern(constituent: string, slot: number, value: string) {
    onChange(
      inflows.map((i) => {
        if (i.constituent !== constituent) return i;
        const patterns2 = [...i.patterns] as DwfInflow["patterns"];
        patterns2[slot] = value;
        return { ...i, patterns: patterns2 };
      })
    );
  }

  function removeRow(constituent: string) {
    onChange(inflows.filter((i) => i.constituent !== constituent));
  }

  /** "FLOW" is a UI label, translated; every other name is a real pollutant, shown exactly as the user named it. */
  function constituentLabel(name: string): string {
    return name === "FLOW" ? t("inflows.typeFlow") : name;
  }

  return (
    <section className="property-panel-group">
      <h3>{t("dwf.title")}</h3>
      {inflows.length === 0 && <p className="property-panel-hint">{t("dwf.noInflows")}</p>}
      {inflows.length > 0 && (
        <div className="dwf-cards">
          {inflows.map((inflow) => (
            <div className="dwf-card" key={inflow.constituent}>
              <div className="dwf-card-header">
                <select
                  value={inflow.constituent}
                  onChange={(e) => setConstituent(inflow.constituent, e.target.value)}
                >
                  <option value={inflow.constituent}>{constituentLabel(inflow.constituent)}</option>
                  {availableToAdd.map((name) => (
                    <option key={name} value={name}>
                      {constituentLabel(name)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="button-icon button-icon-danger"
                  onClick={() => removeRow(inflow.constituent)}
                  aria-label={t("dwf.deleteInflow")}
                  title={t("dwf.deleteInflow")}
                >
                  <TrashIcon />
                </button>
              </div>
              <label className="options-field">
                {t("dwf.avgValue")}
                <NumberInput
                  value={inflow.avgValue}
                  onChange={(v) => setAvgValue(inflow.constituent, v)}
                />
              </label>
              <div className="dwf-card-patterns">
                {PATTERN_SLOT_LABEL_KEYS.map((key, slot) => (
                  <label className="options-field" key={key}>
                    {t(key)}
                    <select
                      value={inflow.patterns[slot]}
                      onChange={(e) => setPattern(inflow.constituent, slot, e.target.value)}
                    >
                      <option value="" />
                      {patterns.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="button" onClick={addRow} disabled={availableToAdd.length === 0}>
        {t("dwf.addInflow")}
      </button>
    </section>
  );
}
